import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box, 
  Typography, 
  Paper, 
  CircularProgress, 
  Grid, 
  Button, 
  ButtonGroup, 
  Container,
  TextField,
  InputAdornment,
  Alert,
  Tooltip,
  styled,
  Autocomplete,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  IconButton,
  Divider,
  Select,
  MenuItem,
  Checkbox,
  Snackbar,
  TablePagination
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import InventoryIcon from '@mui/icons-material/Inventory';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterListIcon from '@mui/icons-material/FilterList';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useAuth } from '../../../contexts/AuthContext';
import salesService from '../../../services/salesService';
import { ACCOUNTING_API } from '../../../config/api';
import { accountingApi } from '../../../config/apiClient';
import { useNavigate, useLocation } from 'react-router-dom';
import AccountingDataTable from '../shared/AccountingDataTable';
import type { DataTableColumn } from '../shared/AccountingDataTable';
import * as XLSX from 'xlsx';
import LockIcon from '@mui/icons-material/Lock';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteIcon from '@mui/icons-material/Delete';
import ReceiptIcon from '@mui/icons-material/Receipt';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Menu from '@mui/material/Menu';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import trLocale from 'date-fns/locale/tr';
import enLocale from 'date-fns/locale/en-US';
import InventoryTableSettings from './InventoryTableSettings';
import { useTranslation } from 'react-i18next';

// Baslik alani icin styled component
const HeaderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
}));

// Stok hareket arayuzu
interface StockMovement {
  id: string;
  itemId: string;
  itemName: string;
  movementType: string;
  description: string;
  quantity: number;
  previousQuantity: number;
  newQuantity: number;
  unitPrice: number;
  totalAmount: number;
  date: string;
  createdAt: string;
  invoiceId?: string;
  accountId?: string;
  accountName?: string;
  currency: string;
  exchangeRate: number;
  vatRate?: number;
  sctRate?: number;
  screenId?: string;
  screenName?: string;
  refundedQuantity?: number;
  originalMovementId?: string;
  warehouseId?: string;
  warehouseName?: string;
}

// Urun arayuzu
interface InventoryItem {
  id: string;
  name: string;
  stockCode: string;
  categoryId?: string;
  categoryName?: string;
}

// Kategori arayuzu
interface Category {
  id: string;
  name: string;
  description?: string;
}

// Tablo sutunu arayuzu
interface TableColumn {
  id: string;
  label: string;
  field: string;
  visible: boolean;
  fixed: boolean;
  sortable?: boolean;
  description?: string;
}

// Sabitler
const TRANSACTION_TYPES = {
  SALE: 'SALE',
  PURCHASE: 'PURCHASE',
  TRANSFER_IN: 'TRANSFER_IN',
  TRANSFER_OUT: 'TRANSFER_OUT',
  ADJUSTMENT_IN: 'ADJUSTMENT_IN',
  ADJUSTMENT_OUT: 'ADJUSTMENT_OUT',
  RETURN_SALE: 'RETURN_SALE',
  RETURN_PURCHASE: 'RETURN_PURCHASE',
  PRODUCTION_IN: 'PRODUCTION_IN',
  PRODUCTION_OUT: 'PRODUCTION_OUT',
  QUICK_SALE: 'QUICK_SALE',
  SALES_INVOICE: 'SALES_INVOICE',
  PURCHASE_INVOICE: 'PURCHASE_INVOICE',
  SALES_ORDER: 'SALES_ORDER',
  PURCHASE_ORDER: 'PURCHASE_ORDER'
};

// Hareket turlerinin gorunen isimleri
const MOVEMENT_TYPE_NAMES: { [key: string]: string } = {
  [TRANSACTION_TYPES.SALE]: 'Satış',
  [TRANSACTION_TYPES.PURCHASE]: 'Alış',
  [TRANSACTION_TYPES.TRANSFER_IN]: 'Depo Girişi (Transfer)',
  [TRANSACTION_TYPES.TRANSFER_OUT]: 'Depo Çıkışı (Transfer)',
  [TRANSACTION_TYPES.ADJUSTMENT_IN]: 'Stok Düzeltme (Giriş)',
  [TRANSACTION_TYPES.ADJUSTMENT_OUT]: 'Stok Düzeltme (Çıkış)',
  [TRANSACTION_TYPES.RETURN_SALE]: 'Satış İadesi',
  [TRANSACTION_TYPES.RETURN_PURCHASE]: 'Alış İadesi',
  [TRANSACTION_TYPES.PRODUCTION_IN]: 'Üretimden Giriş',
  [TRANSACTION_TYPES.PRODUCTION_OUT]: 'Üretime Sarf',
  [TRANSACTION_TYPES.QUICK_SALE]: 'Hızlı Satış',
  [TRANSACTION_TYPES.SALES_INVOICE]: 'Satış Faturası',
  [TRANSACTION_TYPES.PURCHASE_INVOICE]: 'Alış Faturası',
  [TRANSACTION_TYPES.SALES_ORDER]: 'Satış Siparişi',
  [TRANSACTION_TYPES.PURCHASE_ORDER]: 'Alış Siparişi',
  'MANUAL_ADJUSTMENT': 'Manuel Stok Düzeltme'
};

// Iade verileri arayuzu
interface RefundData {
  movementId: string;
  itemId: string;
  itemName: string;
  originalQuantity: number;
  refundQuantity: number;
  unitPrice: number;
  totalRefundAmount: number;
  accountId: string;
  accountName: string;
  addToCustomerBalance: boolean;
}

// Iade islemi icin modal arayuzu
interface RefundModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (refundData: RefundData) => void;
  movement: StockMovement | null;
}

// Iade Modali Bileseni
const RefundModal: React.FC<RefundModalProps> = ({ open, onClose, onConfirm, movement }) => {
  const [refundQuantity, setRefundQuantity] = useState<number>(0);
  const [addToCustomerBalance, setAddToCustomerBalance] = useState<boolean>(true);
  const [processing, setProcessing] = useState<boolean>(false);

  useEffect(() => {
    if (open && movement) {
      const alreadyRefundedQuantity = movement.refundedQuantity || 0;
      const remainingQuantity = movement.quantity - alreadyRefundedQuantity;
      setRefundQuantity(remainingQuantity);
      setAddToCustomerBalance(true);
      setProcessing(false);
    }
  }, [open, movement]);

  const calculateRefundAmount = (): number => {
    if (!movement) return 0;
    return refundQuantity * movement.unitPrice;
  };

  const maxRefundQuantity = movement ? (movement.quantity - (movement.refundedQuantity || 0)) : 0;

  const handleRefundQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value);
    if (isNaN(value)) {
      setRefundQuantity(0);
    } else {
      setRefundQuantity(Math.min(value, maxRefundQuantity));
    }
  };

  const handleConfirm = () => {
    if (!movement) return;
    setProcessing(true);
    const refundData: RefundData = {
      movementId: movement.id,
      itemId: movement.itemId,
      itemName: movement.itemName,
      originalQuantity: movement.quantity,
      refundQuantity: refundQuantity,
      unitPrice: movement.unitPrice,
      totalRefundAmount: calculateRefundAmount(),
      accountId: movement.accountId || '',
      accountName: movement.accountName || '',
      addToCustomerBalance: addToCustomerBalance
    };
    onConfirm(refundData);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Ürün İade İşlemi
      </DialogTitle>
      <DialogContent>
        {!movement ? (
          <CircularProgress />
        ) : (
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle1" fontWeight="bold">
                  İade Edilecek Ürün Bilgileri
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Ürün:
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {movement.itemName}
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Cari Hesap:
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {movement.accountName || 'Belirtilmemiş'}
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Satış Miktarı:
                </Typography>
                <Typography variant="body1">
                  {movement.quantity}
                </Typography>
                {movement.refundedQuantity && movement.refundedQuantity > 0 && (
                  <Typography variant="body2" color="text.secondary">
                    (Daha önce {movement.refundedQuantity} adet iade edilmiş)
                  </Typography>
                )}
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  Birim Fiyat:
                </Typography>
                <Typography variant="body1" color="primary" fontWeight="medium">
                  {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: movement.currency }).format(movement.unitPrice)}
                  {movement.vatRate ? (
                    <Typography component="span" variant="caption" color="text.secondary">
                      {' '}(KDV %{movement.vatRate})
                    </Typography>
                  ) : null}
                </Typography>
              </Grid>
              
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="İade Edilecek Miktar"
                  type="number"
                  value={refundQuantity}
                  onChange={handleRefundQuantityChange}
                  inputProps={{
                    min: 0.01,
                    max: maxRefundQuantity,
                    step: 0.01
                  }}
                  disabled={processing}
                  required
                  helperText={`Maksimum ${maxRefundQuantity} adet iade edilebilir`}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">
                  İade Tutarı:
                </Typography>
                <Typography variant="h6" color="primary" fontWeight="bold">
                  {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: movement.currency }).format(calculateRefundAmount())}
                </Typography>
              </Grid>
              
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={addToCustomerBalance}
                      onChange={(e) => setAddToCustomerBalance(e.target.checked)}
                      disabled={processing}
                    />
                  }
                  label="İade tutarını müşteri hesabına ekle"
                />
              </Grid>
              
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mt: 1 }}>
                  Bu işlem, ürünü stoğa geri ekleyecek ve bir iade hareketi oluşturacaktır.
                  {addToCustomerBalance && movement.accountName && (
                    <span> Ayrıca, {movement.accountName} hesabına {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: movement.currency }).format(calculateRefundAmount())} tutarında bakiye eklenecektir.</span>
                  )}
                </Alert>
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button 
          onClick={onClose} 
          disabled={processing}
          color="inherit"
        >
          İptal
        </Button>
        <Button 
          onClick={handleConfirm} 
          disabled={!movement || refundQuantity <= 0 || refundQuantity > maxRefundQuantity || processing}
          variant="contained" 
          color="primary"
        >
          {processing ? <CircularProgress size={24} /> : "İadeyi Onayla"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const StockMovementsReport: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userDetails } = useAuth();
  const { t, i18n } = useTranslation('accounting');
  const locale = i18n.language?.toLowerCase().startsWith('en') ? 'en-US' : 'tr-TR';
  const dateLocale = i18n.language?.toLowerCase().startsWith('en') ? enLocale : trLocale;
  
  // Server-side pagination state
  const [movements, setMovements] = useState<StockMovement[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [sortColumn, setSortColumn] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [aggregates, setAggregates] = useState<{ totalSaleQuantity: number; totalAmount: number; totalQuantityChange: number }>({
    totalSaleQuantity: 0, totalAmount: 0, totalQuantityChange: 0
  });
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [itemSearchTerm, setItemSearchTerm] = useState('');
  const [selectedMovementType, setSelectedMovementType] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [isTableSettingsModalOpen, setIsTableSettingsModalOpen] = useState(false);
  
  // Iade islemi icin state tanimlari
  const [isRefundModalOpen, setIsRefundModalOpen] = useState(false);
  const [selectedMovementForRefund, setSelectedMovementForRefund] = useState<StockMovement | null>(null);
  const [isProcessingRefund, setIsProcessingRefund] = useState(false);
  const [refundQuantity, setRefundQuantity] = useState(0);
  const [addToCustomerBalance, setAddToCustomerBalance] = useState(true);
  
  // Satis ekranlari icin state tanimi (dropdown doldurmak icin)
  const [salesScreens, setSalesScreens] = useState<{ id: string; name: string; type: string }[]>([]);
  // Secili satis ekranlari icin state tanimi
  const [selectedSalesScreens, setSelectedSalesScreens] = useState<string[]>([]);
  // Tarih araligi icin state tanimlari
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  
  // Filtreleri goster/gizle icin state
  const [showFilters, setShowFilters] = useState(false);
  
  // Excel export icin loading state
  const [loadingExport, setLoadingExport] = useState(false);
  const [showInfoBox, setShowInfoBox] = useState(false);
  
  // Snackbar icin state tanimlari
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('success');
  
  // 3 nokta menusu icin state tanimlari
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedMovementForMenu, setSelectedMovementForMenu] = useState<StockMovement | null>(null);
  
  // Silme modali icin state tanimlari
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedMovementForDelete, setSelectedMovementForDelete] = useState<StockMovement | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Debounce timer ref'leri
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initialLoadDone = useRef(false);
  
  // Tablo sutunlari
  const [tableColumns, setTableColumns] = useState<TableColumn[]>([
    { id: 'rowNumber', label: t('stock.report.columns.rowNumber'), field: 'rowNumber', visible: true, fixed: true, sortable: false, description: t('stock.report.columnsDesc.rowNumber') },
    { id: 'date', label: t('stock.report.columns.date'), field: 'createdAt', visible: true, fixed: true, sortable: true, description: t('stock.report.columnsDesc.date') },
    { id: 'movementType', label: t('stock.report.columns.movementType'), field: 'movementType', visible: true, fixed: true, sortable: true, description: t('stock.report.columnsDesc.movementType') },
    { id: 'screenName', label: t('stock.report.columns.screenName'), field: 'screenName', visible: false, fixed: false, sortable: true, description: t('stock.report.columnsDesc.screenName') },
    { id: 'item', label: t('stock.report.columns.item'), field: 'itemName', visible: true, fixed: true, sortable: true, description: t('stock.report.columnsDesc.item') },
    { id: 'account', label: t('stock.report.columns.account'), field: 'accountName', visible: true, fixed: false, sortable: true, description: t('stock.report.columnsDesc.account') },
    { id: 'description', label: t('stock.report.columns.description'), field: 'description', visible: false, fixed: false, sortable: true, description: t('stock.report.columnsDesc.description') },
    { id: 'previousQuantity', label: t('stock.report.columns.previousQuantity'), field: 'previousQuantity', visible: false, fixed: false, sortable: true, description: t('stock.report.columnsDesc.previousQuantity') },
    { id: 'quantityChange', label: t('stock.report.columns.quantityChange'), field: 'quantityChange', visible: true, fixed: false, sortable: true, description: t('stock.report.columnsDesc.quantityChange') },
    { id: 'newQuantity', label: t('stock.report.columns.newQuantity'), field: 'newQuantity', visible: true, fixed: false, sortable: true, description: t('stock.report.columnsDesc.newQuantity') },
    { id: 'warehouseName', label: t('stock.report.columns.warehouseName'), field: 'warehouseName', visible: true, fixed: false, sortable: true, description: t('stock.report.columnsDesc.warehouseName') },
    { id: 'saleQuantity', label: t('stock.report.columns.saleQuantity'), field: 'quantity', visible: false, fixed: false, sortable: true, description: t('stock.report.columnsDesc.saleQuantity') },
    { id: 'unitPrice', label: t('stock.report.columns.unitPrice'), field: 'unitPrice', visible: false, fixed: false, sortable: true, description: t('stock.report.columnsDesc.unitPrice') },
    { id: 'totalAmount', label: t('stock.report.columns.totalAmount'), field: 'totalAmount', visible: true, fixed: true, sortable: true, description: t('stock.report.columnsDesc.totalAmount') },
    { id: 'actions', label: t('stock.report.columns.actions'), field: 'actions', visible: true, fixed: true, sortable: false, description: t('stock.report.columnsDesc.actions') },
  ]);
  
  // LocalStorage key
  const STOCK_MOVEMENTS_TABLE_SETTINGS_KEY = 'stockMovementsTableSettings';

  // Gelen state varsa
  useEffect(() => {
    if (location.state && (location.state as any).searchTerm) {
      const incomingSearchTerm = (location.state as any).searchTerm;
      console.log('\uD83D\uDD0D [REPORT] Gelen arama terimi:', incomingSearchTerm);
      setSearchTerm(incomingSearchTerm);
    }
  }, [location.state]);

  // LocalStorage'dan tablo ayarlarini yukle
  useEffect(() => {
    const savedSettings = localStorage.getItem(STOCK_MOVEMENTS_TABLE_SETTINGS_KEY);
    if (savedSettings) {
      try {
        const parsedSettings = JSON.parse(savedSettings);
        const updatedColumns = tableColumns.map(defaultColumn => {
          const savedColumn = parsedSettings.find((col: TableColumn) => col.id === defaultColumn.id);
          if (savedColumn) {
            return {
              ...defaultColumn,
              visible: defaultColumn.fixed ? true : savedColumn.visible,
              sortable: savedColumn.sortable !== undefined ? savedColumn.sortable : defaultColumn.sortable
            };
          }
          return defaultColumn;
        });
        setTableColumns(updatedColumns);
      } catch (e) {
        console.error('Kayıtlı tablo ayarları yüklenirken hata:', e);
      }
    }
  }, []);

  // Tablo ayarlarini kaydet
  const saveTableSettings = (columns: TableColumn[]) => {
    setTableColumns(columns);
    localStorage.setItem(STOCK_MOVEMENTS_TABLE_SETTINGS_KEY, JSON.stringify(columns));
  };

  // ════════════════════════════════════════════════════════════════
  // Server-side paged veri yukleme fonksiyonu
  // ════════════════════════════════════════════════════════════════
  const loadMovementsPage = useCallback(async (
    cId: string,
    pageNum: number,
    pageSz: number,
    search: string,
    itemSearch: string,
    movementType: string | null,
    category: Category | null,
    screenIds: string[] | null,
    sDate: Date | null,
    eDate: Date | null,
    screensParam?: { id: string; name: string; type: string }[],
    sortCol?: string,
    sortDir?: string
  ) => {
    try {
      setLoading(true);
      setError(null);
      
      // Arama terimlerini belirle
      let searchParam = '';
      if (search.trim() && itemSearch.trim()) {
        searchParam = search.trim();
      } else if (search.trim()) {
        searchParam = search.trim();
      } else if (itemSearch.trim()) {
        searchParam = itemSearch.trim();
      }

      // Tarih filtrelerini UTC'ye cevir
      let startDateISO: string | undefined;
      let endDateISO: string | undefined;
      if (sDate) startDateISO = sDate.toISOString();
      if (eDate) endDateISO = eDate.toISOString();

      // screenId: Ilk secili ekrani gonder
      let screenIdParam: string | undefined;
      if (screenIds && screenIds.length > 0) screenIdParam = screenIds[0];

      const url = ACCOUNTING_API.STOCK_MOVEMENTS_PAGED({
        page: pageNum,
        pageSize: pageSz,
        search: searchParam || undefined,
        movementType: movementType || undefined,
        startDate: startDateISO,
        endDate: endDateISO,
        categoryId: category?.id || undefined,
        screenId: screenIdParam,
        sortColumn: sortCol || undefined,
        sortDirection: sortDir || undefined,
      });

      console.log(`\uD83D\uDCCB [PAGED] Stok hareketleri yükleniyor: Sayfa=${pageNum}, Boyut=${pageSz}, Arama="${searchParam}", Tip=${movementType}`);

      const response = await accountingApi.get(url);

      const result = response.data;
      if (!result.success) throw new Error(result.message || 'Stok hareketleri yüklenemedi');

      const sqlMovements = result.data || [];
      const screensToUse = screensParam || salesScreens;

      const movementsList: StockMovement[] = sqlMovements.map((data: any) => {
        let screenName: string | null = null;
        
        if (data.movementType === 'QUICK_SALE' || data.movementType === 'quick_sale' || 
            data.movementType === 'SALES_INVOICE' || data.movementType === TRANSACTION_TYPES.SALES_INVOICE) {
          if (data.screenId) {
            const screen = screensToUse.find(s => s.id === data.screenId.toString());
            screenName = screen ? screen.name : t('stock.report.movementTypes.quickSale');
          } else {
            screenName = t('stock.report.movementTypes.quickSale');
          }
        } else if (data.screenId) {
          const screen = screensToUse.find(s => s.id === data.screenId.toString());
          if (screen) screenName = screen.name;
        }
        
        if (!screenName && data.screenName) screenName = data.screenName;
        
        return {
          id: data.id?.toString() || '',
          itemId: data.productId?.toString() || '',
          itemName: data.productName || '',
          movementType: data.movementType || '',
          description: data.description || '',
          quantity: parseFloat(data.quantity) || 0,
          previousQuantity: parseFloat(data.previousQuantity) || 0,
          newQuantity: parseFloat(data.newQuantity) || 0,
          unitPrice: parseFloat(data.unitPrice) || 0,
          totalAmount: parseFloat(data.totalAmount) || 0,
          date: data.date || data.createdAt,
          createdAt: data.createdAt || '',
          invoiceId: data.invoiceId || '',
          accountId: data.accountId?.toString() || '',
          accountName: data.accountName || '',
          currency: data.currency || 'TRY',
          exchangeRate: parseFloat(data.exchangeRate) || 1,
          vatRate: parseFloat(data.vatRate) || 0,
          sctRate: parseFloat(data.sctRate) || 0,
          screenId: data.screenId?.toString() || '',
          screenName: screenName || '-',
          refundedQuantity: parseFloat(data.refundedQuantity) || 0,
          originalMovementId: data.originalMovementId?.toString() || '',
          warehouseId: data.warehouseId?.toString() || '',
          warehouseName: data.warehouseName || '',
        };
      });

      setMovements(movementsList);
      setTotalCount(result.totalCount || 0);
      setTotalPages(result.totalPages || 0);
      setPage(pageNum - 1);
      
      if (result.aggregates) {
        setAggregates({
          totalSaleQuantity: result.aggregates.totalSaleQuantity || 0,
          totalAmount: result.aggregates.totalAmount || 0,
          totalQuantityChange: result.aggregates.totalQuantityChange || 0,
        });
      }

      console.log(`\u2705 [PAGED] ${movementsList.length}/${result.totalCount} stok hareketi yüklendi (Sayfa ${pageNum}/${result.totalPages})`);
      setLoading(false);
    } catch (error) {
      console.error('\u274C Stok hareketleri yüklenirken hata:', error);
      setLoading(false);
      setError('Stok hareketleri yüklenirken bir hata oluştu.');
    }
  }, [salesScreens, t]);

  // Filtre degistiginde sayfa 1'e donup yeni API cagrisi yap
  const triggerFilteredLoad = useCallback((resetPage: boolean = true) => {
    if (!companyId || !initialLoadDone.current) return;
    const targetPage = resetPage ? 1 : page + 1;
    loadMovementsPage(
      companyId, targetPage, rowsPerPage,
      searchTerm, itemSearchTerm,
      selectedMovementType, selectedCategory,
      selectedSalesScreens.length > 0 ? selectedSalesScreens : null,
      startDate, endDate,
      undefined,
      sortColumn, sortDirection
    );
  }, [companyId, page, rowsPerPage, searchTerm, itemSearchTerm, selectedMovementType, selectedCategory, selectedSalesScreens, startDate, endDate, sortColumn, sortDirection, loadMovementsPage]);

  // SQL'den verileri yukleme
  useEffect(() => {
    loadCompanyAndData();
  }, [userDetails?.companyId]);

  const loadCompanyAndData = async () => {
    try {
      setLoading(true);
      
      const cId = userDetails?.companyId;
      if (!cId) {
        setLoading(false);
        setError('Şirket bilgisi bulunamadı.');
        return;
      }
      
      setCompanyId(cId);
      
      const loadedScreens = await loadSalesScreens(cId);
      await Promise.all([
        loadInventoryItems(cId),
        loadInventoryCategories(cId),
      ]);
      
      await loadMovementsPage(cId, 1, rowsPerPage, '', '', null, null, null, null, null, loadedScreens);
      initialLoadDone.current = true;
      
    } catch (error) {
      console.error('Şirket veya veri yüklenirken hata:', error);
      setLoading(false);
      setError('Veriler yüklenirken bir hata oluştu.');
    }
  };

  // Satis ekranlarini yukleme fonksiyonu (SQL'den)
  const loadSalesScreens = async (companyId: string): Promise<{ id: string; name: string; type: string }[]> => {
    try {
      console.log('\uD83D\uDCCB Satış ekranları SQL\'den yükleniyor...');
      const sqlScreens = await salesService.getQuickSaleScreens();
      let screensArray: { id: string; name: string; type: string }[] = [];
      
      if (Array.isArray(sqlScreens)) {
        sqlScreens.forEach((screen: any) => {
          if (screen.IsActive !== false) {
            const screenType = screen.ScreenType || 'Standalone';
            const typeSuffix = screenType === 'Restaurant' ? 'Restoran' : 'Hızlı Satış';
            screensArray.push({
              id: screen.Id.toString(),
              name: `${screen.Name} (${typeSuffix})`,
              type: screenType === 'Restaurant' ? 'restaurant' : 'quicksale'
            });
          }
        });
      }
      
      screensArray.sort((a, b) => a.name.localeCompare(b.name));
      console.log(`\u2705 Toplam ${screensArray.length} satış ekranı SQL'den yüklendi:`, screensArray.map(s => s.name));
      setSalesScreens(screensArray);
      return screensArray;
    } catch (error) {
      console.error('\u274C Satış ekranları SQL\'den yüklenirken hata:', error);
      return [];
    }
  };

  // Stok urunlerini yukleme (SQL'den)
  const loadInventoryItems = async (companyId: string) => {
    try {
      console.log('\uD83D\uDCCB Stok ürünleri SQL\'den yükleniyor...');
      const response = await accountingApi.get(ACCOUNTING_API.INVENTORY_ITEMS);
      
      const result = response.data;
      const sqlItems = result.data || result;
      
      if (!sqlItems || sqlItems.length === 0) {
        console.log('Veritabanında stok ürünü bulunamadı');
        setItems([]);
        return;
      }
      
      const itemsList: InventoryItem[] = sqlItems.map((data: any) => ({
        id: data.id,
        name: data.name || '',
        stockCode: data.stockCode || '',
        categoryId: data.categoryId || '',
        categoryName: data.categoryName || '',
      }));
      
      itemsList.sort((a, b) => a.name.localeCompare(b.name));
      console.log(`\u2705 ${itemsList.length} stok ürünü yüklendi`);
      setItems(itemsList);
    } catch (error) {
      console.error('\u274C Stok ürünleri yüklenirken hata:', error);
    }
  };

  // Kategorileri yukleme (SQL'den)
  const loadInventoryCategories = async (companyId: string) => {
    try {
      console.log('\uD83D\uDCC2 Kategoriler SQL\'den yükleniyor...');
      const response = await accountingApi.get(ACCOUNTING_API.INVENTORY_CATEGORIES);
      
      const result = response.data;
      const sqlCategories = result.data || result;
      
      if (!sqlCategories || sqlCategories.length === 0) {
        console.log('Veritabanında kategori bulunamadı');
        setCategories([]);
        return;
      }
      
      const categoriesList: Category[] = sqlCategories.map((data: any) => ({
        id: data.id,
        name: data.name || '',
        description: data.description || '',
      }));
      
      categoriesList.sort((a, b) => a.name.localeCompare(b.name));
      console.log(`\u2705 ${categoriesList.length} kategori yüklendi`);
      setCategories(categoriesList);
    } catch (error) {
      console.error('\u274C Kategoriler yüklenirken hata:', error);
    }
  };

  // ════════════════════════════════════════════════════════════════
  // Debounced arama: searchTerm ve itemSearchTerm degistiginde 500ms bekleyip API cagir
  // ════════════════════════════════════════════════════════════════
  useEffect(() => {
    if (!initialLoadDone.current) return;
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      triggerFilteredLoad(true);
    }, 500);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [searchTerm, itemSearchTerm]);

  // Filtre degisiklikleri (anlik - debounce yok)
  useEffect(() => {
    if (!initialLoadDone.current) return;
    triggerFilteredLoad(true);
  }, [selectedMovementType, selectedCategory, selectedSalesScreens, startDate, endDate]);

  // Sayfa degistirme
  const handleChangePage = (_event: unknown, newPage: number) => {
    if (!companyId) return;
    loadMovementsPage(
      companyId, newPage + 1, rowsPerPage,
      searchTerm, itemSearchTerm,
      selectedMovementType, selectedCategory,
      selectedSalesScreens.length > 0 ? selectedSalesScreens : null,
      startDate, endDate,
      undefined,
      sortColumn, sortDirection
    );
  };

  // Sayfa basina satir degistirme
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    if (!companyId) return;
    loadMovementsPage(
      companyId, 1, newRowsPerPage,
      searchTerm, itemSearchTerm,
      selectedMovementType, selectedCategory,
      selectedSalesScreens.length > 0 ? selectedSalesScreens : null,
      startDate, endDate,
      undefined,
      sortColumn, sortDirection
    );
  };

  // Server-side sıralama
  const handleSort = (field: string, direction: 'asc' | 'desc') => {
    setSortColumn(field);
    setSortDirection(direction);
    if (!companyId) return;
    loadMovementsPage(
      companyId, 1, rowsPerPage,
      searchTerm, itemSearchTerm,
      selectedMovementType, selectedCategory,
      selectedSalesScreens.length > 0 ? selectedSalesScreens : null,
      startDate, endDate,
      undefined,
      field, direction
    );
  };

  // Geri butonu
  const handleGoBack = () => {
    navigate('/stok');
  };

  // Hareket tipine gore ikon ve renk
  const getMovementTypeIcon = (type: string) => {
    switch (type) {
      case 'sale':
        return <ShoppingBagIcon fontSize="small" color="primary" />;
      case 'manual_sale':
        return <ShoppingBagIcon fontSize="small" color="secondary" />;
      case 'purchase':
        return <ShoppingCartIcon fontSize="small" color="secondary" />;
      case 'adjustment':
        return <AssessmentIcon fontSize="small" color="warning" />;
      case 'transfer':
        return <LocalShippingIcon fontSize="small" color="info" />;
      case 'QUICK_SALE':
      case 'quick_sale':
      case TRANSACTION_TYPES.QUICK_SALE:
        return <ShoppingBagIcon fontSize="small" color="success" />;
      case 'sale_due_to_edit':
        return <ShoppingBagIcon fontSize="small" color="warning" />;
      case 'sale_return_due_to_edit':
        return <ArrowUpwardIcon fontSize="small" color="info" />;
      case 'SALES_INVOICE':
      case TRANSACTION_TYPES.SALES_INVOICE:
        return <ShoppingBagIcon fontSize="small" color="primary" />;
      case 'PURCHASE_INVOICE':
      case TRANSACTION_TYPES.PURCHASE_INVOICE:
        return <ShoppingCartIcon fontSize="small" color="secondary" />;
      case 'SALES_ORDER':
      case TRANSACTION_TYPES.SALES_ORDER:
        return <ShoppingBagIcon fontSize="small" color="info" />;
      case 'PURCHASE_ORDER':
      case TRANSACTION_TYPES.PURCHASE_ORDER:
        return <ShoppingCartIcon fontSize="small" color="info" />;
      default:
        return <InventoryIcon fontSize="small" />;
    }
  };

  // Hareket tipini metne cevir
  const getMovementTypeText = (type: string): string => {
    switch (type) {
      case 'sale': return t('stock.report.movementTypes.sale');
      case 'manual_sale': return t('stock.report.movementTypes.manualSale');
      case 'purchase': return t('stock.report.movementTypes.purchase');
      case 'adjustment': return t('stock.report.movementTypes.adjustment');
      case 'transfer': return t('stock.report.movementTypes.transfer');
      case 'QUICK_SALE': return t('stock.report.movementTypes.quickSale');
      case 'quick_sale': return t('stock.report.movementTypes.quickSale');
      case 'SALES_INVOICE': return t('stock.report.movementTypes.salesInvoice');
      case 'PURCHASE_INVOICE': return t('stock.report.movementTypes.purchaseInvoice');
      case 'SALES_ORDER': return t('stock.report.movementTypes.salesOrder');
      case 'PURCHASE_ORDER': return t('stock.report.movementTypes.purchaseOrder');
      case 'MANUAL_ADJUSTMENT': return t('stock.report.movementTypes.manualAdjustment');
      case 'sale_due_to_edit': return t('stock.report.movementTypes.saleAfterEdit');
      case 'sale_return_due_to_edit': return t('stock.report.movementTypes.returnAfterEdit');
      default: 
        if (MOVEMENT_TYPE_NAMES[type]) {
          return MOVEMENT_TYPE_NAMES[type];
        }
        return type.charAt(0).toUpperCase() + type.slice(1);
    }
  };

  // Islem yonune gore ikon ve renk
  const getQuantityDirectionIcon = (quantity: number) => {
    if (quantity < 0) {
      return <ArrowDownwardIcon fontSize="small" color="error" />;
    } else if (quantity > 0) {
      return <ArrowUpwardIcon fontSize="small" color="success" />;
    } else {
      return null;
    }
  };

  // Miktar degerlerini Turkce formatla goster
  const formatQuantity = (value: number, showSign: boolean = false): string => {
    const rounded = Math.round(value * 10000) / 10000;
    const formatted = rounded.toLocaleString(locale, {
      minimumFractionDigits: 0, 
      maximumFractionDigits: 4 
    });
    if (showSign && rounded > 0) {
      return '+' + formatted;
    }
    return formatted;
  };

  // Tarihi formatla - Turkiye saat dilimi (GMT+3)
  // Guard: Backend DateTime'lari UTC yaziyor ama eski deploy'larda "Z" suffix'i
  // eksik olabilir. Bu durumda tarayici yerel saat olarak yorumlar ve 3 saat kayma
  // olusur. Timezone bilgisi yoksa UTC kabul et.
  const formatDate = (dateString: string): string => {
    if (!dateString) return '-';
    const hasTimezone = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(dateString);
    const normalized = hasTimezone ? dateString : `${dateString}Z`;
    const date = new Date(normalized);
    return new Intl.DateTimeFormat(locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: 'Europe/Istanbul',
    }).format(date);
  };

  // Para formati
  const formatCurrency = (amount: number, currencyCode: string = 'TRY'): string => {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  // Hareket tipine gore renk
  const getMovementTypeChipColor = (type: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (type) {
      case 'sale': return 'primary';
      case 'manual_sale': return 'secondary';
      case 'purchase': return 'secondary';
      case 'adjustment': return 'warning';
      case 'transfer': return 'info';
      case 'QUICK_SALE':
      case 'quick_sale':
      case TRANSACTION_TYPES.QUICK_SALE: return 'success';
      case 'sale_due_to_edit': return 'warning';
      case 'sale_return_due_to_edit': return 'info';
      case 'SALES_INVOICE':
      case TRANSACTION_TYPES.SALES_INVOICE: return 'primary';
      case 'PURCHASE_INVOICE':
      case TRANSACTION_TYPES.PURCHASE_INVOICE: return 'secondary';
      case 'MANUAL_ADJUSTMENT': return 'primary';
      default: return 'default';
    }
  };

  // Tablo ayarlari modalini acma
  const handleOpenTableSettingsModal = () => {
    setIsTableSettingsModalOpen(true);
  };

  const handleCloseTableSettingsModal = () => {
    setIsTableSettingsModalOpen(false);
  };

  const handleUpdateTableSettings = (columns: TableColumn[]) => {
    saveTableSettings(columns);
  };

  // AccountingDataTable icin dinamik sutun tanimlari
  const movementColumns = useMemo<DataTableColumn<StockMovement>[]>(() => {
    return tableColumns
      .filter(col => col.visible)
      .map(col => {
        const labelMap: Record<string, string> = {
          rowNumber: t('stock.report.columns.rowNumber'),
          date: t('stock.report.columns.date'),
          movementType: t('stock.report.columns.movementType'),
          screenName: t('stock.report.columns.screenName'),
          item: t('stock.report.columns.item'),
          account: t('stock.report.columns.account'),
          description: t('stock.report.columns.description'),
          previousQuantity: t('stock.report.columns.previousQuantity'),
          quantityChange: t('stock.report.columns.quantityChange'),
          newQuantity: t('stock.report.columns.newQuantity'),
          warehouseName: t('stock.report.columns.warehouseName'),
          saleQuantity: t('stock.report.columns.saleQuantity'),
          unitPrice: t('stock.report.columns.unitPrice'),
          totalAmount: t('stock.report.columns.totalAmount'),
          actions: t('stock.report.columns.actions'),
        };
        const base: DataTableColumn<StockMovement> = {
          id: col.id,
          label: labelMap[col.id] || col.label,
          field: col.field as keyof StockMovement,
          sortable: col.sortable !== false && col.id !== 'actions' && col.id !== 'rowNumber',
        };
        switch (col.id) {
          case 'rowNumber':
            return { ...base, width: '60px', render: (_row: StockMovement, index?: number) => {
              const globalIndex = page * rowsPerPage + (index !== undefined ? index + 1 : 0);
              return globalIndex || '-';
            }};
          case 'date':
            return { ...base, render: (row: StockMovement) => formatDate(row.createdAt) };
          case 'movementType':
            return { ...base, render: (row: StockMovement) => (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {getMovementTypeIcon(row.movementType)}
                <Chip label={getMovementTypeText(row.movementType)} size="small" color={getMovementTypeChipColor(row.movementType)} variant="outlined" />
              </Box>
            )};
          case 'screenName':
            return { ...base, render: (row: StockMovement) => row.screenName || '-' };
          case 'item':
            return { ...base, render: (row: StockMovement) => (
              <Tooltip title={t('stock.report.tooltips.stockCodeDetail')}>
                <Typography variant="body2" fontWeight="medium">{row.itemName}</Typography>
              </Tooltip>
            )};
          case 'account':
            return { ...base, render: (row: StockMovement) => row.accountName || '-' };
          case 'description':
            return { ...base, render: (row: StockMovement) => {
              return (row.description || '-')
                .replace(/SALES_INVOICE/g, t('stock.report.movementTypes.salesInvoice'))
                .replace(/PURCHASE_INVOICE/g, t('stock.report.movementTypes.purchaseInvoice'))
                .replace(/QUICK_SALE/g, t('stock.report.movementTypes.quickSale'));
            }};
          case 'previousQuantity':
            return { ...base, render: (row: StockMovement) => formatQuantity(row.previousQuantity) };
          case 'quantityChange':
            return { ...base, render: (row: StockMovement) => {
              const qtyChange = row.newQuantity - row.previousQuantity;
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {getQuantityDirectionIcon(qtyChange)}
                  <Typography component="span" sx={{ fontWeight: 'bold', color: qtyChange < 0 ? 'error.main' : qtyChange > 0 ? 'success.main' : 'text.primary' }}>
                    {formatQuantity(qtyChange, true)}
                  </Typography>
                </Box>
              );
            }};
          case 'newQuantity':
            return { ...base, render: (row: StockMovement) => formatQuantity(row.newQuantity) };
          case 'warehouseName':
            return { ...base, render: (row: StockMovement) => row.warehouseName || '-' };
          case 'saleQuantity':
            return { ...base, render: (row: StockMovement) => {
              if (row.movementType === 'sale' || row.movementType === 'manual_sale' || 
                  row.movementType === 'QUICK_SALE' || row.movementType === 'quick_sale' || 
                  row.movementType === TRANSACTION_TYPES.QUICK_SALE) {
                return formatQuantity(row.quantity);
              }
              return '-';
            }};
          case 'unitPrice':
            return { ...base, render: (row: StockMovement) => {
              return formatCurrency(row.unitPrice, row.currency);
            }};
          case 'totalAmount':
            return { ...base, render: (row: StockMovement) => {
              return formatCurrency(row.totalAmount, row.currency);
            }};
          case 'actions':
            return { ...base, render: (row: StockMovement) => (
              <IconButton size="small" onClick={(e) => handleMenuOpen(e, row)} sx={{ color: 'text.secondary' }}>
                <MoreVertIcon fontSize="small" />
              </IconButton>
            )};
          default:
            return base;
        }
      });
  }, [tableColumns, page, rowsPerPage, t]);

  // Filtre temizleme fonksiyonlari
  const handleClearMovementTypeFilter = () => {
    setSelectedMovementType(null);
  };

  const handleClearItemFilter = () => {
    setItemSearchTerm('');
  };

  const handleClearCategoryFilter = () => {
    setSelectedCategory(null);
  };

  const handleClearSalesScreensFilter = () => {
    setSelectedSalesScreens([]);
  };

  const handleClearDateFilter = () => {
    setStartDate(null);
    setEndDate(null);
  };

  const handleClearAllFilters = () => {
    setSearchTerm('');
    setItemSearchTerm('');
    setSelectedMovementType(null);
    setSelectedCategory(null);
    setSelectedSalesScreens([]);
    setStartDate(null);
    setEndDate(null);
  };

  // Iade islemini baslatma fonksiyonu
  const handleRefund = async (movement: StockMovement) => {
    console.log('İade işlemi başlatıldı:', movement);
    const currentMovement = movements.find(m => m.id === movement.id);
    const alreadyRefundedQuantity = currentMovement?.refundedQuantity || movement.refundedQuantity || 0;
    
    const updatedMovement = {
      ...movement,
      refundedQuantity: alreadyRefundedQuantity
    };
    
    if (alreadyRefundedQuantity >= updatedMovement.quantity) {
      setSnackbarMessage('Bu ürün zaten tamamen iade edilmiş.');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }
    
    const remainingQuantity = updatedMovement.quantity - alreadyRefundedQuantity;
    setSelectedMovementForRefund(updatedMovement);
    setRefundQuantity(remainingQuantity);
    setAddToCustomerBalance(true);
    setIsRefundModalOpen(true);
  };

  // Iade tutarini hesapla
  const calculateRefundAmount = (): number => {
    if (!selectedMovementForRefund) return 0;
    return refundQuantity * selectedMovementForRefund.unitPrice;
  };

  // Iade islemini onaylama fonksiyonu
  const handleConfirmRefund = async () => {
    if (!companyId || !selectedMovementForRefund) {
      setSnackbarMessage('Şirket bilgisi veya ürün bilgisi bulunamadı.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    try {
      setIsProcessingRefund(true);
      
      const refundData: RefundData = {
        movementId: selectedMovementForRefund.id,
        itemId: selectedMovementForRefund.itemId,
        itemName: selectedMovementForRefund.itemName,
        originalQuantity: selectedMovementForRefund.quantity,
        refundQuantity: refundQuantity,
        unitPrice: selectedMovementForRefund.unitPrice,
        totalRefundAmount: calculateRefundAmount(),
        accountId: selectedMovementForRefund.accountId || '',
        accountName: selectedMovementForRefund.accountName || '',
        addToCustomerBalance: addToCustomerBalance
      };
      
      console.log('İade işlemi onaylandı:', refundData);

      const alreadyRefundedQuantity = selectedMovementForRefund.refundedQuantity || 0;
      const totalRefundedQuantity = alreadyRefundedQuantity + refundData.refundQuantity;
      
      if (totalRefundedQuantity > refundData.originalQuantity) {
        throw new Error(`İade miktarı geçersiz. Maksimum ${refundData.originalQuantity - alreadyRefundedQuantity} adet iade edilebilir.`);
      }

      // 1. Ilgili urunun guncel stok miktarini SQL API'den al
      const itemResponse = await accountingApi.get(ACCOUNTING_API.INVENTORY_ITEM_BY_ID(refundData.itemId));
      
      const itemData = itemResponse.data;
      const currentQuantity = parseFloat(itemData.quantity) || 0;
      const newQuantity = currentQuantity + refundData.refundQuantity;

      // 3. Iade stok hareketini SQL API ile olustur
      const refundDate = new Date();
      const refundMovementPayload = {
        companyId: companyId,
        productId: refundData.itemId,
        warehouseId: selectedMovementForRefund.warehouseId || '',
        direction: 'IN' as const,
        quantity: refundData.refundQuantity,
        unitPrice: refundData.unitPrice,
        referenceNumber: refundData.movementId,
        description: `${refundData.itemName} ürünü iadesi - Orijinal hareket: ${refundData.movementId}`,
        date: refundDate.toISOString(),
        createdBy: (userDetails?.firstName && userDetails?.lastName ? `${userDetails.firstName} ${userDetails.lastName}` : userDetails?.email) || 'Bilinmeyen Kullanıcı',
      };
      
      const movementResponse = await accountingApi.post(ACCOUNTING_API.STOCK_MOVEMENT_CREATE_MANUAL, refundMovementPayload);
      
      const movementResult = movementResponse.data;
      console.log('\u2705 İade stok hareketi oluşturuldu:', movementResult);

      // 4. Musteri hesabina ekleme
      if (refundData.addToCustomerBalance && refundData.accountId) {
        const accountResponse = await accountingApi.get(ACCOUNTING_API.ACCOUNT_BY_ID(parseInt(refundData.accountId)));
        
        const accountData = accountResponse.data;
        console.log(`[İade] Müşteri bakiyesi güncelleniyor: İade Tutarı=${refundData.totalRefundAmount}`);
        
        const nowDate = new Date();
        const dateForDB = nowDate.toISOString().split('T')[0];
        
        const collectionPayload = {
          companyId: companyId,
          accountId: parseInt(refundData.accountId),
          amount: refundData.totalRefundAmount,
          date: dateForDB,
          description: `${refundData.itemName} ürünü iadesi (${refundData.refundQuantity} adet)`,
          paymentMethod: 'refund',
          currency: 'TRY',
          exchangeRate: 1,
          notes: `Satış iadesi: ${refundData.refundQuantity} adet ürün, toplam ${refundData.totalRefundAmount.toFixed(2)} TL`,
          referenceType: 'stockMovement',
          referenceId: movementResult.movementId || null,
        };
        
        try {
          const transactionResponse = await accountingApi.post(ACCOUNTING_API.TRANSACTION_COLLECTION, collectionPayload);
          console.log(`[İade] Cari hareket kaydedildi:`, transactionResponse.data);
        } catch (txError) {
          console.error('[İade] Cari hareket kaydedilemedi');
        }
      }
      
      // State guncelle ve sayfayi yeniden yukle
      setIsRefundModalOpen(false);
      setSelectedMovementForRefund(null);
      setIsProcessingRefund(false);
      
      setSnackbarMessage(`${refundData.itemName} ürünü başarıyla iade edildi. ${refundData.refundQuantity} adet ürün stoğa eklendi.`);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      
      // Sayfayi yenile
      triggerFilteredLoad(false);
      
    } catch (error) {
      console.error('İade işlemi sırasında hata:', error);
      setSnackbarMessage(`İade işlemi sırasında bir hata oluştu: ${error instanceof Error ? error.message : 'Bilinmeyen hata'}`);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      setIsProcessingRefund(false);
    }
  };

  // Snackbar kapatma islemi
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // 3 nokta menusu islemleri
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, movement: StockMovement) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedMovementForMenu(movement);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedMovementForMenu(null);
  };

  // Silme modali islemleri
  const handleOpenDeleteModal = () => {
    if (selectedMovementForMenu) {
      setSelectedMovementForDelete(selectedMovementForMenu);
      setIsDeleteModalOpen(true);
    }
    handleMenuClose();
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setSelectedMovementForDelete(null);
  };

  // Silme islemi
  const handleDeleteMovement = async () => {
    if (!selectedMovementForDelete) return;
    
    setIsDeleting(true);
    try {
      console.log('\uD83D\uDDD1\uFE0F Silinecek hareket:', selectedMovementForDelete);
      
      const response = await accountingApi.post(ACCOUNTING_API.STOCK_MOVEMENT_DELETE, {
        movementId: selectedMovementForDelete.id,
        companyId: companyId
      });
      
      const result = response.data;
      
      if (result.success) {
        console.log('\u2705 Stok hareketi silindi:', selectedMovementForDelete.id);
        
        setSnackbarMessage('Stok hareketi başarıyla silindi.');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        handleCloseDeleteModal();
        
        // Sayfayi yenile
        triggerFilteredLoad(false);
      } else {
        throw new Error(result.message || 'Silme işlemi başarısız oldu.');
      }
    } catch (error: any) {
      console.error('\u274C Silme hatası:', error);
      setSnackbarMessage(error.message || 'Silme işlemi başarısız oldu.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setIsDeleting(false);
    }
  };

  // Excel'e aktarma fonksiyonu (server-side export)
  const exportToExcel = async () => {
    try {
      setLoadingExport(true);
      
      if (!companyId) {
        setSnackbarMessage('Şirket bilgisi bulunamadı.');
        setSnackbarSeverity('warning');
        setSnackbarOpen(true);
        setLoadingExport(false);
        return;
      }

      console.log('\uD83D\uDCCA [EXCEL EXPORT] Excel dışa aktarım başladı (server-side export)');

      // Arama terimlerini belirle
      let searchParam = '';
      if (searchTerm.trim()) searchParam = searchTerm.trim();
      else if (itemSearchTerm.trim()) searchParam = itemSearchTerm.trim();

      let startDateISO: string | undefined;
      let endDateISO: string | undefined;
      if (startDate) startDateISO = startDate.toISOString();
      if (endDate) endDateISO = endDate.toISOString();

      let screenIdParam: string | undefined;
      if (selectedSalesScreens.length > 0) screenIdParam = selectedSalesScreens[0];

      const url = ACCOUNTING_API.STOCK_MOVEMENTS_PAGED({
        page: 1,
        pageSize: 50000,
        search: searchParam || undefined,
        movementType: selectedMovementType || undefined,
        startDate: startDateISO,
        endDate: endDateISO,
        categoryId: selectedCategory?.id || undefined,
        screenId: screenIdParam,
        export: true,
      });

      const response = await accountingApi.get(url);

      const result = response.data;
      const exportMovements = result.data || [];

      if (exportMovements.length === 0) {
        setSnackbarMessage('Aktarılacak hareket bulunamadı.');
        setSnackbarSeverity('warning');
        setSnackbarOpen(true);
        setLoadingExport(false);
        return;
      }

      // Excel icin veri hazirligi
      const excelData = exportMovements.map((data: any, index: number) => ({
        'Sıra': index + 1,
        'Tarih': formatDate(data.createdAt || ''),
        'İşlem Tipi': getMovementTypeText(data.movementType || ''),
        'Satış Ekranı': (() => {
          if (data.screenId) {
            const screen = salesScreens.find(s => s.id === data.screenId.toString());
            return screen ? screen.name : '-';
          }
          return data.screenName || '-';
        })(),
        'Ürün': data.productName || '',
        'Cari': data.accountName || '-',
        'Açıklama': data.description || '-',
        'Önceki Miktar': parseFloat(data.previousQuantity) || 0,
        'Miktar Değişimi': (parseFloat(data.newQuantity) || 0) - (parseFloat(data.previousQuantity) || 0),
        'Yeni Miktar': parseFloat(data.newQuantity) || 0,
        'Birim Fiyat': parseFloat(data.unitPrice) || 0,
        'Toplam Tutar': parseFloat(data.totalAmount) || 0,
        'Para Birimi': data.currency || 'TRY',
        'KDV Oranı (%)': parseFloat(data.vatRate) || 0,
      }));
      
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      
      const columnWidths = [
        { wch: 8 },
        { wch: 18 },
        { wch: 20 },
        { wch: 25 },
        { wch: 30 },
        { wch: 25 },
        { wch: 40 },
        { wch: 15 },
        { wch: 15 },
        { wch: 15 },
        { wch: 20 },
        { wch: 20 },
        { wch: 12 },
        { wch: 15 },
      ];
      worksheet['!cols'] = columnWidths;
      
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Stok Hareketleri');
      
      const now = new Date();
      const dateStr = `${now.getDate()}.${now.getMonth() + 1}.${now.getFullYear()}`;
      const fileName = `Stok_Hareket_Raporu_${dateStr}.xlsx`;
      
      XLSX.writeFile(workbook, fileName);
      
      console.log(`\u2705 [EXCEL EXPORT] ${exportMovements.length} adet stok hareketi Excel dosyasına aktarıldı.`);
      setSnackbarMessage(`${exportMovements.length} hareket başarıyla Excel'e aktarıldı`);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      
    } catch (error) {
      console.error('\u274C [EXCEL EXPORT] Excel dışa aktarım hatası:', error);
      setSnackbarMessage('Excel dışa aktarım sırasında bir hata oluştu.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoadingExport(false);
    }
  };

  return (
    <Box component="main" sx={{ p: 3 }}>
      {/* Üst Başlık */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleGoBack}
            sx={{ mr: 2 }}
          >
            {t('stock.report.actions.back')}
          </Button>
          <Typography variant="h5" component="h1" fontWeight="bold">
            {t('stock.report.title')}
          </Typography>
        </Box>
      </Box>

      {/* Arama/Filtre ve Butonlar - Aynı Satır */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          {/* Arama Alanı */}
          <Grid item xs={5.5} md={2.5}>
            <TextField
              fullWidth
              size="small"
              placeholder={t('stock.report.filters.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          
          {/* Ürün Arama */}
          <Grid item xs={5.5} md={2.5}>
            <TextField
              fullWidth
              size="small"
              placeholder={t('stock.report.filters.productSearchPlaceholder')}
              value={itemSearchTerm}
              onChange={(e) => setItemSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <InventoryIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          
          {/* Filtreleri Göster İkonu */}
          <Grid item xs={1} md={0.5} sx={{ display: 'flex', justifyContent: 'center' }}>
            <Tooltip title={showFilters ? t('stock.actions.hideFilters') : t('stock.actions.showFilters')}>
              <IconButton
                color={showFilters ? "primary" : "default"}
                onClick={() => setShowFilters(!showFilters)}
                size="small"
              >
                {showFilters ? <ExpandLessIcon /> : <FilterListIcon />}
              </IconButton>
            </Tooltip>
          </Grid>

          {/* Sağ taraf - Butonlar */}
          <Grid item xs={12} md={6.5} sx={{ display: 'flex', justifyContent: { xs: 'center', md: 'flex-end' }, gap: 1, flexWrap: 'wrap' }}>
            <Tooltip title={t('stock.report.actions.info')}>
              <IconButton
                size="small"
                color={showInfoBox ? 'primary' : 'default'}
                onClick={() => setShowInfoBox(prev => !prev)}
              >
                <InfoOutlinedIcon />
              </IconButton>
            </Tooltip>
            <ButtonGroup size="small">
              <Button 
                variant="outlined" 
                startIcon={<AssessmentIcon />}
                onClick={exportToExcel}
                disabled={loadingExport || loading || totalCount === 0}
              >
                {loadingExport ? t('stock.report.actions.downloading') : t('stock.menu.downloadExcel')}
              </Button>
              <Button
                variant="outlined" 
                startIcon={<ViewColumnIcon />}
                onClick={handleOpenTableSettingsModal}
              >
                {t('stock.menu.customizeTable')}
              </Button>
            </ButtonGroup>
            
            {(searchTerm || itemSearchTerm || selectedMovementType || selectedCategory || selectedSalesScreens.length > 0 || startDate || endDate) && (
              <Button
                size="small"
                variant="outlined"
                color="secondary"
                onClick={handleClearAllFilters}
              >
                {t('stock.report.actions.clear')}
              </Button>
            )}
          </Grid>
        </Grid>

        {showInfoBox && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
              {t('stock.report.infoBox.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              {t('stock.report.infoBox.description')}
            </Typography>
            <Typography variant="body2">
              {t('stock.report.infoBox.item1')}
            </Typography>
            <Typography variant="body2">
              {t('stock.report.infoBox.item2')}
            </Typography>
            <Typography variant="body2">
              {t('stock.report.infoBox.item3')}
            </Typography>
          </Alert>
        )}
        
        {/* Gizlenebilir Filtreler */}
        {showFilters && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={2}>
                <Autocomplete
                  fullWidth
                  size="small"
                  options={['sale', 'purchase', 'adjustment', 'transfer', 'manual_sale', 'QUICK_SALE', 'SALES_INVOICE', 'PURCHASE_INVOICE']}
                  getOptionLabel={(option) => getMovementTypeText(option)}
                  value={selectedMovementType}
                  onChange={(_, newValue) => setSelectedMovementType(newValue)}
                  renderInput={(params) => (
                    <TextField 
                      {...params} 
                      label={t('stock.report.filters.movementType')}
                      placeholder={t('stock.report.filters.movementTypePlaceholder')}
                      size="small"
                    />
                  )}
                />
              </Grid>
              
              <Grid item xs={12} md={2}>
                <Autocomplete
                  fullWidth
                  size="small"
                  options={categories}
                  getOptionLabel={(option) => option.name}
                  value={selectedCategory}
                  onChange={(_, newValue) => setSelectedCategory(newValue)}
                  renderInput={(params) => (
                    <TextField 
                      {...params} 
                      label={t('stock.report.filters.category')}
                      placeholder={t('stock.report.filters.categoryPlaceholder')}
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <FilterAltIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
              
              <Grid item xs={12} md={2}>
                <Autocomplete
                  fullWidth
                  multiple
                  size="small"
                  options={salesScreens}
                  getOptionLabel={(option) => option.name}
                  value={salesScreens.filter(screen => selectedSalesScreens.includes(screen.id))}
                  onChange={(_, newValue) => {
                    setSelectedSalesScreens(newValue.map(screen => screen.id));
                  }}
                  renderTags={(tagValue, getTagProps) =>
                    tagValue.map((option, index) => (
                      <Chip
                        label={option.name}
                        {...getTagProps({ index })}
                        size="small"
                        color="primary"
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField 
                      {...params} 
                      label={t('stock.report.filters.salesScreen')}
                      placeholder={t('stock.report.filters.salesScreenPlaceholder')}
                      size="small"
                    />
                  )}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateLocale}>
                  <Box sx={{ display: 'flex', gap: { xs: 1, md: 2 }, alignItems: 'center', flexDirection: { xs: 'column', md: 'row' } }}>
                    <DateTimePicker
                      label={t('stock.report.filters.startDate')}
                      value={startDate}
                      onChange={(newValue) => setStartDate(newValue)}
                      format="dd.MM.yyyy HH:mm"
                      ampm={false}
                      slotProps={{ 
                        textField: { 
                          fullWidth: true,
                          size: 'small',
                          InputProps: {
                            startAdornment: (
                              <InputAdornment position="start">
                                <CalendarTodayIcon fontSize="small" />
                              </InputAdornment>
                            ),
                          }
                        } 
                      }}
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', md: 'block' } }}>-</Typography>
                    <DateTimePicker
                      label={t('stock.report.filters.endDate')}
                      value={endDate}
                      onChange={(newValue) => setEndDate(newValue)}
                      format="dd.MM.yyyy HH:mm"
                      ampm={false}
                      slotProps={{ 
                        textField: { 
                          fullWidth: true,
                          size: 'small',
                          InputProps: {
                            startAdornment: (
                              <InputAdornment position="start">
                                <CalendarTodayIcon fontSize="small" />
                              </InputAdornment>
                            ),
                          }
                        } 
                      }}
                    />
                    {(startDate || endDate) && (
                      <IconButton 
                        size="small" 
                        color="secondary" 
                        onClick={handleClearDateFilter}
                        title={t('stock.report.actions.clearDateFilter')}
                        sx={{ alignSelf: { xs: 'flex-end', md: 'center' } }}
                      >
                        <FilterAltIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                </LocalizationProvider>
              </Grid>
            </Grid>
          </Box>
        )}
      </Box>

      {/* Ana İçerik */}
      <Box sx={{ mb: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {loading ? (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '400px' 
          }}>
            <CircularProgress />
          </Box>
        ) : movements.length === 0 ? (
          <Box sx={{ 
            p: 4, 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            justifyContent: 'center',
            height: '400px',
            background: 'linear-gradient(135deg, #f5f7fa 0%, #e8ecf2 100%)',
            borderRadius: 2
          }}>
            <InventoryIcon 
              sx={{ 
                fontSize: 80, 
                color: 'primary.main',
                mb: 2
              }} 
            />
            
            <Typography variant="h5" component="h2" gutterBottom color="primary.main" fontWeight="bold">
              {t('stock.report.empty.title')}
            </Typography>
            
            <Typography variant="body1" color="text.secondary">
              {searchTerm || itemSearchTerm || selectedMovementType || selectedCategory || selectedSalesScreens.length > 0 || startDate || endDate
                ? t('stock.empty.searchNotFound')
                : t('stock.report.empty.noRecords')
              }
            </Typography>
          </Box>
        ) : (
          <>
            <AccountingDataTable<StockMovement>
              columns={movementColumns}
              rows={movements}
              getRowId={(row) => row.id}
              loading={false}
              pagination={false}
              emptyMessage={t('stock.report.empty.noRows')}
              emptyIcon={<InventoryIcon sx={{ fontSize: 48 }} />}
              ariaLabel={t('stock.report.tableAriaLabel')}
              onSort={handleSort}
              sortField={sortColumn}
              sortDirection={sortDirection}
              columnResizeStorageKey="stock-movements-report"
            />
            {/* Server-side Sayfalama */}
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[25, 50, 100]}
              labelRowsPerPage={t('stock.pagination.rowsPerPage')}
              labelDisplayedRows={({ from, to, count }) =>
                t('stock.report.pagination.displayedRows', { count, from, to })
              }
            />
            {/* Toplam Satırı */}
            {totalCount > 0 && (
              <Paper elevation={1} sx={{ mt: 1, p: 1.5, borderTop: '2px solid', borderTopColor: 'primary.main' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                  <Typography variant="body2" fontWeight="bold" color="primary.dark">
                    {t('stock.report.summary.totalMovements', { count: totalCount })}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">{t('stock.report.summary.change')}</Typography>
                      {getQuantityDirectionIcon(aggregates.totalQuantityChange)}
                      <Typography variant="body2" fontWeight="bold" sx={{ color: aggregates.totalQuantityChange < 0 ? 'error.main' : aggregates.totalQuantityChange > 0 ? 'success.main' : 'text.primary' }}>
                        {formatQuantity(aggregates.totalQuantityChange, true)}
                      </Typography>
                    </Box>
                    {aggregates.totalSaleQuantity > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2" color="text.secondary">{t('stock.report.summary.sales')}</Typography>
                        <Typography variant="body2" fontWeight="bold">{formatQuantity(aggregates.totalSaleQuantity)}</Typography>
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">{t('stock.report.summary.total')}</Typography>
                      <Typography variant="body2" fontWeight="bold">{formatCurrency(aggregates.totalAmount, 'TRY')}</Typography>
                    </Box>
                  </Box>
                </Box>
              </Paper>
            )}
          </>
        )}
        
        {/* Sayfa altına ek boşluk */}
        <Box sx={{ height: '200px' }} />
      </Box>

      {/* Tablo Ayarları Modalı */}
      <InventoryTableSettings
        open={isTableSettingsModalOpen}
        onClose={handleCloseTableSettingsModal}
        onSave={handleUpdateTableSettings}
        currentColumns={tableColumns}
      />

      {/* İade Modalı */}
      <Dialog 
        open={isRefundModalOpen} 
        onClose={() => {
          if (!isProcessingRefund) {
            setIsRefundModalOpen(false);
            setSelectedMovementForRefund(null);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Ürün İade İşlemi
        </DialogTitle>
        <DialogContent>
          {!selectedMovementForRefund ? (
            <CircularProgress />
          ) : (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    İade Edilecek Ürün Bilgileri
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Ürün:
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {selectedMovementForRefund.itemName}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Cari Hesap:
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {selectedMovementForRefund.accountName || 'Belirtilmemiş'}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Satış Miktarı:
                  </Typography>
                  <Typography variant="body1">
                    {selectedMovementForRefund.quantity}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Birim Fiyat:
                  </Typography>
                  <Typography variant="body1" color="primary" fontWeight="medium">
                    {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: selectedMovementForRefund.currency }).format(selectedMovementForRefund.unitPrice)}
                    {selectedMovementForRefund.vatRate ? (
                      <Typography component="span" variant="caption" color="text.secondary">
                        {' '}(KDV %{selectedMovementForRefund.vatRate})
                      </Typography>
                    ) : null}
                  </Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="İade Edilecek Miktar"
                    type="number"
                    value={refundQuantity}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value);
                      if (isNaN(value)) {
                        setRefundQuantity(0);
                      } else {
                        setRefundQuantity(Math.min(value, selectedMovementForRefund.quantity));
                      }
                    }}
                    inputProps={{
                      min: 0.01,
                      max: selectedMovementForRefund.quantity,
                      step: 0.01
                    }}
                    disabled={isProcessingRefund}
                    required
                    helperText={`Maksimum ${selectedMovementForRefund.quantity} adet iade edilebilir`}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    İade Tutarı:
                  </Typography>
                  <Typography variant="h6" color="primary" fontWeight="bold">
                    {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: selectedMovementForRefund.currency }).format(calculateRefundAmount())}
                  </Typography>
                </Grid>
                
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={addToCustomerBalance}
                        onChange={(e) => setAddToCustomerBalance(e.target.checked)}
                        disabled={isProcessingRefund}
                      />
                    }
                    label="İade tutarını müşteri hesabına ekle"
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ mt: 1 }}>
                    Bu işlem, ürünü stoğa geri ekleyecek ve bir iade hareketi oluşturacaktır.
                    {addToCustomerBalance && selectedMovementForRefund.accountName && (
                      <span> Ayrıca, {selectedMovementForRefund.accountName} hesabına {new Intl.NumberFormat('tr-TR', { style: 'currency', currency: selectedMovementForRefund.currency }).format(calculateRefundAmount())} tutarında bakiye eklenecektir.</span>
                    )}
                  </Alert>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setIsRefundModalOpen(false);
              setSelectedMovementForRefund(null);
            }} 
            disabled={isProcessingRefund}
            color="inherit"
          >
            İptal
          </Button>
          <Button 
            onClick={handleConfirmRefund} 
            disabled={!selectedMovementForRefund || refundQuantity <= 0 || refundQuantity > (selectedMovementForRefund?.quantity || 0) || isProcessingRefund}
            variant="contained" 
            color="primary"
          >
            {isProcessingRefund ? <CircularProgress size={24} /> : "İadeyi Onayla"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 3 Nokta Menüsü */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          elevation: 3,
          sx: { minWidth: '180px' }
        }}
      >
        <MenuItem onClick={handleOpenDeleteModal}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText primary={t('stock.common.delete')} sx={{ color: 'error.main' }} />
        </MenuItem>
      </Menu>

      {/* Silme Onay Modalı */}
      <Dialog
        open={isDeleteModalOpen}
        onClose={!isDeleting ? handleCloseDeleteModal : undefined}
        maxWidth="sm"
        fullWidth
      >
        {(() => {
          const isIntegrationTransaction = 
            selectedMovementForDelete?.movementType === 'QUICK_SALE' || 
            selectedMovementForDelete?.movementType === 'quick_sale' ||
            selectedMovementForDelete?.movementType === 'SALES_INVOICE' ||
            selectedMovementForDelete?.movementType === 'PURCHASE_INVOICE' ||
            /SF-\d{6}-\d{4}|AF-\d{6}-\d{4}/.test(selectedMovementForDelete?.description || '');
          
          const invoiceNumberMatch = selectedMovementForDelete?.description?.match(/[SA]F-\d{6}-\d{4}/);
          const extractedInvoiceNumber = invoiceNumberMatch ? invoiceNumberMatch[0] : null;

          return (
            <>
              <DialogTitle
                sx={{
                  bgcolor: isIntegrationTransaction ? 'warning.main' : 'error.main',
                  color: 'white'
                }}
              >
                {isIntegrationTransaction ? '\u26A0\uFE0F Entegrasyon Hareketi' : 'Stok Hareketi Sil'}
              </DialogTitle>
              <DialogContent>
                {isIntegrationTransaction ? (
                  <>
                    <Alert severity="error" sx={{ mb: 2, mt: 2 }}>
                      <Typography variant="body2" fontWeight="bold">
                        🚫 Bu bir entegrasyon hareketidir ve buradan silinemez!
                      </Typography>
                    </Alert>
                    
                    <Alert severity="info" sx={{ mb: 2 }}>
                      <Typography variant="body2">
                        Bu işlem bir fatura ile ilişkili olduğundan, doğrudan bu ekrandan silinemez. 
                        Bu işlemi silmek istiyorsanız lütfen <strong>Fatura Yönetimi</strong> modülünden 
                        ilgili faturayı bulup siliniz.
                      </Typography>
                      {extractedInvoiceNumber && (
                        <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
                          🔄 Fatura Numarası: {extractedInvoiceNumber}
                        </Typography>
                      )}
                    </Alert>

                    {selectedMovementForDelete && (
                      <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1 }}>
                        <Typography variant="body2" gutterBottom>
                          <strong>Ürün:</strong> {selectedMovementForDelete.itemName}
                        </Typography>
                        <Typography variant="body2" gutterBottom>
                          <strong>Miktar:</strong> {selectedMovementForDelete.quantity}
                        </Typography>
                        <Typography variant="body2" gutterBottom>
                          <strong>İşlem Tipi:</strong> {getMovementTypeText(selectedMovementForDelete.movementType)}
                        </Typography>
                        <Typography variant="body2" gutterBottom>
                          <strong>Açıklama:</strong> {selectedMovementForDelete.description || '-'}
                        </Typography>
                      </Box>
                    )}
                  </>
                ) : (
                  <>
                    <Alert severity="warning" sx={{ mb: 2, mt: 2 }}>
                      ⚠️ <strong>DİKKAT:</strong> Bu işlem sadece <strong>hareket kaydını siler</strong>, 
                      stok miktarları güncellenmez. Bu işlem <strong>geri alınamaz!</strong>
                    </Alert>
                    {selectedMovementForDelete && (
                      <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1 }}>
                        <Typography variant="body2" gutterBottom>
                          <strong>Ürün:</strong> {selectedMovementForDelete.itemName}
                        </Typography>
                        <Typography variant="body2" gutterBottom>
                          <strong>Miktar:</strong> {selectedMovementForDelete.quantity}
                        </Typography>
                        <Typography variant="body2" gutterBottom>
                          <strong>İşlem Tipi:</strong> {getMovementTypeText(selectedMovementForDelete.movementType)}
                        </Typography>
                        <Typography variant="body2" gutterBottom>
                          <strong>Açıklama:</strong> {selectedMovementForDelete.description || '-'}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 2, color: 'error.main', fontWeight: 'bold' }}>
                          Bu işlemi silmek istediğinize emin misiniz?
                        </Typography>
                      </Box>
                    )}
                  </>
                )}
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={handleCloseDeleteModal} disabled={isDeleting}>
                  {isIntegrationTransaction ? 'Kapat' : 'İptal'}
                </Button>
                
                {isIntegrationTransaction ? (
                  <Button
                    onClick={() => {
                      handleCloseDeleteModal();
                      navigate('/fatura/satis');
                    }}
                    variant="contained"
                    color="primary"
                    startIcon={<ReceiptIcon />}
                  >
                    Fatura Yönetimine Git
                  </Button>
                ) : (
                  <Button
                    onClick={handleDeleteMovement}
                    color="error"
                    variant="contained"
                    disabled={isDeleting}
                    startIcon={isDeleting ? <CircularProgress size={20} /> : <DeleteIcon />}
                  >
                    {isDeleting ? 'Siliniyor...' : 'Sil'}
                  </Button>
                )}
              </DialogActions>
            </>
          );
        })()}
      </Dialog>

      {/* Snackbar bildirimi */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StockMovementsReport; 
